import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

var _expressionsKeys$map$;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import config from './config/ordering';
var variants = config.variants; // Variant helpers

export var typeMapper = function typeMapper(type) {
  return function (value) {
    return {
      type: type,
      value: value
    };
  };
};
export var defaultMapper = function defaultMapper(attrType) {
  return function (_ref) {
    var attributes = _ref.attributes;

    var _attributes$find = attributes.find(function (attr) {
      return attr.type === attrType;
    }),
        type = _attributes$find.type,
        value = _attributes$find.value;

    return typeMapper(type)(value);
  };
};
/**
 * Returns a filter function which, when given an array of products, filters
 * out any products which don't have the given attribute type and value.
 *
 * e.g. [].filter(variantFilter('Color', 'Black')) will filter out all
 * products which don't have the Black Color attribute
 */

export var variantFilter = function variantFilter(type, value) {
  return function (product) {
    var hasMatchingAttribute = product.attributes.some(function (attribute) {
      return attribute.type === type && attribute.value === value;
    });
    return hasMatchingAttribute;
  };
}; // Map helpers

var toExpression = function toExpression(exp) {
  return new RegExp("^".concat(exp, "$"), 'i');
};

var toInnerType = function toInnerType(type) {
  return [type, variants[type]];
};

var toExpressionType = function toExpressionType(_ref2) {
  var _ref3 = _slicedToArray(_ref2, 2),
      exp = _ref3[0],
      _ref3$ = _ref3[1],
      order = _ref3$ === void 0 ? 'fixed' : _ref3$;

  return [toExpression(exp), order];
};

var toTypedArray = function toTypedArray(_ref4) {
  var _ref5 = _slicedToArray(_ref4, 2),
      type = _ref5[0],
      list = _ref5[1];

  return [type, list.map(toExpressionType)];
}; // Reduce helpers


var fromTypedArray = [function (result, _ref6) {
  var _ref7 = _slicedToArray(_ref6, 2),
      key = _ref7[0],
      value = _ref7[1];

  return _objectSpread(_objectSpread({}, result), {}, _defineProperty({}, key, value));
}, {}]; // Expression keys

var expressionsKeys = Object.keys(variants); // Expressions map

var expressionsMap = (_expressionsKeys$map$ = expressionsKeys.map(toInnerType).map(toTypedArray)).reduce.apply(_expressionsKeys$map$, fromTypedArray); // Sorting helpers


var numberSort = function numberSort(current, next) {
  return parseFloat(next, 10) - parseFloat(current, 10);
};

var lengthSort = function lengthSort(current, next) {
  return next.length - current.length;
};

var alphabetSort = function alphabetSort(current, next) {
  if (next === current) return 0;
  return next < current ? 1 : -1;
};

var diffSort = function diffSort(current, next) {
  // eslint-disable-next-line no-restricted-globals
  var isInt = function isInt() {
    for (var _len = arguments.length, numbers = new Array(_len), _key = 0; _key < _len; _key++) {
      numbers[_key] = arguments[_key];
    }

    return numbers.every(function (number) {
      return isFinite(number);
    });
  };

  var isSameFirstLetter = function isSameFirstLetter(first, second) {
    return first[0] === second[0];
  }; // Sort integer values


  if (isInt(current, next)) return numberSort(current, next); // Sort otherwise

  return isSameFirstLetter(current, next) ? lengthSort(current, next) : alphabetSort(current, next);
};
/**
 * Apply matching comparator
 * @method applyMatching
 */


var applyMatching = function applyMatching(
/**
 * @param  {Object}    comparators  A map of all available comparators
 * @return {Function}
 */
comparators) {
  return function (
  /**
   * @param  {Array}     comparator  List of comparators to apply
   * @param  {RegExp}    exp         The expression to check against
   * @return {Function}              Array.sort() compare function
   */
  comparator, exp) {
    return function (next, current) {
      // Type safety for values
      var all = [].concat(comparator);
      var first = exp.exec(next).slice(1);
      var second = exp.exec(current).slice(1); // Evaluate and find first decisive comparison

      var evaluate = function evaluate(comp, index) {
        return comparators[comp](first[index], second[index]);
      };

      return all.map(evaluate).find(function (value) {
        return value !== 0;
      }) || 0;
    };
  };
}; // Linter doesn't support recursive definitions


var comparators = {};
/**
 * Nested sort based on RegExp match
 * @method nest
 */

var nest = function nest(
/*
 * @param  {String}    type  The type of variant
 * @return {Function}        Array.sort() comparator function
 */
type) {
  return function (current, next) {
    var keyIndex = expressionsKeys.map(toExpression).findIndex(function (key) {
      return key.test(type);
    });
    var expressions = expressionsMap[expressionsKeys[keyIndex]];

    var testExp = function testExp(str) {
      return function (_ref8) {
        var _ref9 = _slicedToArray(_ref8, 1),
            expression = _ref9[0];

        return expression.test(str);
      };
    };

    var getIndex = function getIndex(str) {
      return expressions.findIndex(testExp(str));
    };

    var firstIndex = getIndex(current);
    var indexDiff = getIndex(next) - firstIndex; // Sort by index in config map

    if (indexDiff !== 0) return indexDiff; // Otherwise apply ordering function

    var _expressions$firstInd = _slicedToArray(expressions[firstIndex], 2),
        exp = _expressions$firstInd[0],
        comp = _expressions$firstInd[1];

    return applyMatching(comparators)(comp, exp)(current, next);
  };
};
/**
 * Comparators map
 */


comparators = {
  fixed: function fixed() {
    return 0;
  },
  asc: function asc(current, next) {
    return diffSort(current, next);
  },
  desc: function desc(current, next) {
    return diffSort(next, current);
  },
  // Recurvise methods for nested types
  colour: nest('colour'),
  size: nest('size'),
  service: nest('service'),
  'bed size': nest('bed size')
};
/**
 * Sorting helper for product variants
 * @method sortVariants
 */

export var sortVariants = function sortVariants(
/**
 * @param  {Function}  mapper  Mapper to use for object
 * @return {Function}          Array.sort() comparator function
 */
mapper) {
  return function (current, next) {
    var _mapper = mapper(current),
        _mapper$type = _mapper.type,
        type = _mapper$type === void 0 ? '' : _mapper$type,
        a = _mapper.value;

    var _mapper2 = mapper(next),
        b = _mapper2.value;

    return nest(type)(b, a);
  };
};
/**
 * Sorting attributes by type
 */

export var sortAttributes = function sortAttributes(current, next) {
  var a = current.type;
  var b = next.type;

  var find = function find(value) {
    return expressionsKeys.map(toExpression).findIndex(function (exp) {
      return exp.test(value);
    });
  };

  return find(a) - find(b);
};